import { StateCreator } from 'zustand';
import { createLens } from '@dhmk/zustand-lens';
import type { EntryType } from '@morph-mapper/types';

export type Suggestion = {
  value: any;
  type: EntryType;
  snippet: any;
  autofill?: boolean;
  source?: string;
  /** Quality given by the Sort Suggestions plugin; higher is better. */
  score?: number;
};

/**
 * Set by a re-evaluation (the dialog, or after an iterator change): the
 * suggestion was found by it (`latest`), or only kept from before because
 * it was applied or still valid (`earlier`). Ordinary evaluations, such as
 * opening the suggestions, leave the marks as they are.
 */
export type SuggestionFound = 'latest' | 'earlier';

export type StoredSuggestion = Suggestion & {
  applied: boolean;
  found?: SuggestionFound;
};

/** Identity of a suggestion: same type, value and snippet. */
export const suggestionKey = ({ type, value, snippet }: Suggestion) =>
  JSON.stringify([type, value, snippet]);

export type FieldEvaluationStatus = 'queued' | 'running' | 'done' | 'error';

export type FieldEvaluation = {
  status: FieldEvaluationStatus;
  updatedAt: number;
  error?: string;
};

export interface AiSuggestionsSlice {
  /** Whether the user has interacted (clicks etc.) — prevents auto-apply. */
  userInteracted: boolean;
  /** Map of `entryId::textContent` keys that have already been auto-applied. */
  appliedKeys: Record<string, true>;
  /** The textContent that was last seen during an auto-apply reset cycle. */
  lastTextContent: string | null;
  /** True while a forced bootstrap is running (file change). */
  rebootstrapInProgress: boolean;
  /** Monotonically increasing counter bumped when rebootstrap completes. */
  bootstrapVersion: number;
  /** ALL Suggestions per entry id. */
  suggestions: Record<string, StoredSuggestion[]>;
  /** Evaluation status per entry id (not saved with the template). */
  fieldStatus: Record<string, FieldEvaluation>;
  /**
   * Fields whose suggestions may be outdated, e.g. because the iterator
   * above them changed. Cleared when the field is evaluated again.
   */
  staleFields: Record<string, true>;
  /** Whether the re-evaluation dialog is open. */
  reevaluateOpen: boolean;

  setUserInteracted: (value: boolean) => void;
  addAppliedKey: (key: string) => void;
  hasAppliedKey: (key: string) => boolean;
  clearAppliedKeys: () => void;
  setLastTextContent: (tc: string | null) => void;
  setRebootstrapInProgress: (value: boolean) => void;
  /** Call when rebootstrap completes to bump version and clear the in-progress flag. */
  completeRebootstrap: () => void;
  /** Reset guards for a new file (clears keys, userInteracted, lastTextContent). */
  resetForNewFile: (textContent: string | null) => void;

  markSuggestionApplied: (entryId: string, suggestion: Suggestion) => void;
  loadSuggestions: (suggestions: Record<string, StoredSuggestion[]>) => void;
  /** Stores the merged result of an evaluation as the field's suggestions. */
  replaceSuggestions: (entryId: string, list: StoredSuggestion[]) => void;
  setFieldStatus: (
    entryId: string,
    status: FieldEvaluationStatus,
    error?: string,
  ) => void;
  markStale: (entryIds: string[]) => void;
  clearStale: (entryIds: string[]) => void;
  setReevaluateOpen: (open: boolean) => void;
}

export const createAiSuggestionsSlice: StateCreator<
  { ai: AiSuggestionsSlice },
  [['zustand/immer', never]],
  [],
  AiSuggestionsSlice
> = (_set, _get) => {
  const [set, get] = createLens(_set, _get, ['ai']);

  return {
    userInteracted: false,
    appliedKeys: {},
    lastTextContent: null,
    rebootstrapInProgress: false,
    bootstrapVersion: 0,
    suggestions: {},
    fieldStatus: {},
    staleFields: {},
    reevaluateOpen: false,

    setUserInteracted: (value) =>
      set((s) => {
        s.userInteracted = value;
      }),

    addAppliedKey: (key) => {
      const current = get().appliedKeys;
      if (current[key]) return;
      set((s) => {
        s.appliedKeys[key] = true;
      });
    },
    hasAppliedKey: (key) => Boolean(get().appliedKeys[key]),
    clearAppliedKeys: () =>
      set((s) => {
        s.appliedKeys = {};
      }),

    setLastTextContent: (tc) =>
      set((s) => {
        s.lastTextContent = tc;
      }),
    setRebootstrapInProgress: (value) =>
      set((s) => {
        s.rebootstrapInProgress = value;
      }),

    completeRebootstrap: () =>
      set((s) => {
        s.rebootstrapInProgress = false;
        s.bootstrapVersion = s.bootstrapVersion + 1;
      }),

    resetForNewFile: (textContent) =>
      set((s) => {
        s.userInteracted = false;
        s.appliedKeys = {};
        s.lastTextContent = textContent;
      }),

    markSuggestionApplied: (entryId, suggestion) =>
      set((s) => {
        const key = suggestionKey(suggestion);
        const list = s.suggestions[entryId] ?? [];
        const match = list.find((prev) => suggestionKey(prev) === key);
        if (match) {
          match.applied = true;
        } else {
          list.push({ ...suggestion, applied: true });
          s.suggestions[entryId] = list;
        }
      }),

    loadSuggestions: (suggestions) =>
      set((s) => {
        s.suggestions = suggestions;
        s.fieldStatus = {};
        s.staleFields = {};
      }),

    replaceSuggestions: (entryId, list) =>
      set((s) => {
        s.suggestions[entryId] = list;
      }),

    setFieldStatus: (entryId, status, error) =>
      set((s) => {
        s.fieldStatus[entryId] = { status, updatedAt: Date.now(), error };
      }),

    markStale: (entryIds) =>
      set((s) => {
        entryIds.forEach((id) => {
          s.staleFields[id] = true;
        });
      }),

    clearStale: (entryIds) => {
      const stale = get().staleFields;
      if (!entryIds.some((id) => stale[id])) return;
      set((s) => {
        entryIds.forEach((id) => {
          delete s.staleFields[id];
        });
      });
    },

    setReevaluateOpen: (open) =>
      set((s) => {
        s.reevaluateOpen = open;
      }),
  };
};
